import { useState, useMemo, type JSX } from 'react'
import {
  Activity,
  DollarSign,
  Search,
  Check,
  RotateCcw,
  Loader2,
  SlidersHorizontal,
} from 'lucide-react'
import ResourceDrawer from '../../ResourceDrawer.js'
import { DrawerTabs } from '../versioned-config-ui.js'
import type { BudgetActivityEvent } from './ActivityTable.js'
import type { PricingStatusItem } from './PricingStatusCard.js'

export interface ActivityPricingDrawerProps {
  open: boolean
  onClose: () => void
  activeTab: 'activity' | 'pricing'
  onTabChange: (tab: 'activity' | 'pricing') => void

  // Activity Props
  events: BudgetActivityEvent[]
  loadingEvents?: boolean
  periodLabel?: string

  // Pricing Props
  pricingItems: PricingStatusItem[]
  loadingPricing?: boolean
  onSavePricing: (
    provider: string,
    model: string,
    inputPer1k: number,
    outputPer1k: number,
  ) => Promise<void>
  onDeletePricing?: ((provider: string, model: string) => Promise<void>) | undefined
  savingPricing?: boolean
}

type ActivityFilterChip = 'all' | 'trips' | 'config' | 'delivery'

export default function ActivityPricingDrawer({
  open,
  onClose,
  activeTab,
  onTabChange,
  events,
  loadingEvents = false,
  periodLabel = 'Oct 2026',
  pricingItems,
  loadingPricing = false,
  onSavePricing,
  onDeletePricing,
  savingPricing = false,
}: ActivityPricingDrawerProps): JSX.Element {
  // Activity state
  const [activityChip, setActivityChip] = useState<ActivityFilterChip>('all')

  // Pricing search & inline edit state
  const [pricingSearch, setPricingSearch] = useState('')
  const [editingModelKey, setEditingModelKey] = useState<string | null>(null)
  const [editInputRate, setEditInputRate] = useState('')
  const [editOutputRate, setEditOutputRate] = useState('')
  const [revertingKey, setRevertingKey] = useState<string | null>(null)

  // Filtered activity events
  const filteredEvents = useMemo(() => {
    if (activityChip === 'all') return events
    if (activityChip === 'trips') {
      return events.filter(
        (ev) => ev.type === 'budget_trip' || ev.type === 'rate_limit_trip',
      )
    }
    if (activityChip === 'config') {
      return events.filter((ev) => ev.type === 'config_change')
    }
    if (activityChip === 'delivery') {
      return events.filter(
        (ev) =>
          ev.type === 'provider' ||
          ev.type === 'pricing_warning' ||
          ev.detail.toLowerCase().includes('delivery') ||
          ev.action.toLowerCase().includes('delivery'),
      )
    }
    return events
  }, [events, activityChip])

  // Filtered pricing items
  const filteredPricingItems = useMemo(() => {
    const q = pricingSearch.trim().toLowerCase()
    if (!q) return pricingItems
    return pricingItems.filter(
      (m) => m.model.toLowerCase().includes(q) || m.provider.toLowerCase().includes(q),
    )
  }, [pricingItems, pricingSearch])

  function handleStartEdit(item: PricingStatusItem): void {
    setEditingModelKey(`${item.provider}:${item.model}`)
    setEditInputRate(String(item.inputPer1k))
    setEditOutputRate(String(item.outputPer1k))
  }

  function handleCancelEdit(): void {
    setEditingModelKey(null)
    setEditInputRate('')
    setEditOutputRate('')
  }

  async function handleSaveEdit(item: PricingStatusItem): Promise<void> {
    const inRate = parseFloat(editInputRate)
    const outRate = parseFloat(editOutputRate)
    if (Number.isNaN(inRate) || inRate < 0 || Number.isNaN(outRate) || outRate < 0) return
    await onSavePricing(item.provider, item.model, inRate, outRate)
    setEditingModelKey(null)
  }

  async function handleRevertPrice(item: PricingStatusItem): Promise<void> {
    if (!onDeletePricing) return
    const key = `${item.provider}:${item.model}`
    setRevertingKey(key)
    try {
      await onDeletePricing(item.provider, item.model)
      if (editingModelKey === key) setEditingModelKey(null)
    } finally {
      setRevertingKey(null)
    }
  }

  function getActivityDotColor(type: BudgetActivityEvent['type']): string {
    switch (type) {
      case 'rate_limit_trip':
      case 'budget_trip':
        return 'bg-red-500'
      case 'config_change':
        return 'bg-blue-500'
      case 'pricing_warning':
      case 'provider':
        return 'bg-amber-500'
      default:
        return 'bg-gray-400'
    }
  }

  function getActivityBadgeStyle(action: string): string {
    const a = action.toLowerCase()
    if (a.includes('block')) {
      return 'bg-red-50 text-red-700 border-red-200'
    }
    if (a.includes('audit')) {
      return 'bg-blue-50 text-blue-700 border-blue-200'
    }
    if (a.includes('flag') || a.includes('alert')) {
      return 'bg-amber-50 text-amber-700 border-amber-200'
    }
    if (a.includes('deliver')) {
      return 'bg-emerald-50 text-emerald-700 border-emerald-200'
    }
    return 'bg-gray-50 text-gray-700 border-gray-200'
  }

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title="Budget Activity & Pricing"
      icon={<SlidersHorizontal className="h-4 w-4 text-white" />}
      width="standard"
    >
      <DrawerTabs
        tabs={[
          {
            key: 'activity' as const,
            label: 'Activity',
            icon: <Activity className="h-3.5 w-3.5" />,
          },
          {
            key: 'pricing' as const,
            label: 'Pricing',
            icon: <DollarSign className="h-3.5 w-3.5" />,
          },
        ]}
        active={activeTab}
        onChange={onTabChange}
      />

      {/* ── TAB 1: Activity ── */}
      {activeTab === 'activity' && (
        <div className="flex flex-col justify-between space-y-4">
          <div>
            {/* Filter Chips */}
            <div className="flex items-center gap-1.5 pb-2">
              {(
                [
                  { id: 'all', label: 'All' },
                  { id: 'trips', label: 'Trips' },
                  { id: 'config', label: 'Config' },
                  { id: 'delivery', label: 'Delivery' },
                ] as const
              ).map((chip) => {
                const isActive = activityChip === chip.id
                return (
                  <button
                    key={chip.id}
                    type="button"
                    onClick={() => setActivityChip(chip.id)}
                    className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors ${
                      isActive
                        ? 'bg-[#3454cf] text-white shadow-2xs'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                    }`}
                  >
                    {chip.label}
                  </button>
                )
              })}
            </div>

            {/* Event Cards */}
            <div className="mt-2 space-y-2.5">
              {loadingEvents ? (
                <div className="py-12 text-center text-xs text-gray-400">
                  <Loader2 className="mx-auto mb-1.5 h-4 w-4 animate-spin" />
                  Loading activity events…
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="rounded-md border border-gray-200 bg-gray-50/50 py-8 text-center text-xs text-gray-400">
                  No activity events found for this filter.
                </div>
              ) : (
                filteredEvents.map((ev) => (
                  <div
                    key={ev.id}
                    className="rounded-md border border-gray-200 bg-white p-3 shadow-2xs transition-colors hover:border-gray-300"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-900">
                        <span
                          className={`h-2 w-2 rounded-full ${getActivityDotColor(ev.type)}`}
                        />
                        <span>{ev.typeLabel}</span>
                      </div>
                      <span
                        className={`rounded-xs border px-1.5 py-0.5 text-[10px] font-semibold ${getActivityBadgeStyle(
                          ev.action,
                        )}`}
                      >
                        {ev.action}
                      </span>
                    </div>

                    <p className="mt-1.5 text-xs leading-relaxed text-gray-700">
                      {ev.detail}
                    </p>

                    <div className="mt-2 text-[11px] text-gray-400">
                      {ev.timeFormatted}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Activity Drawer Footer */}
          <div className="border-t border-gray-100 pt-3 text-[11px] text-gray-400">
            Showing events for {periodLabel} · Older events in History
          </div>
        </div>
      )}

      {/* ── TAB 2: Pricing ── */}
      {activeTab === 'pricing' && (
        <div className="flex flex-col justify-between space-y-4">
          <div>
            {/* Search Input */}
            <div className="relative mb-3">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={pricingSearch}
                onChange={(e) => setPricingSearch(e.target.value)}
                placeholder="Search models or providers…"
                className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-xs text-gray-900 placeholder:text-gray-400 focus:border-[#3454cf] focus:outline-hidden"
              />
            </div>

            {/* Model Pricing Cards */}
            <div className="space-y-2.5">
              {loadingPricing ? (
                <div className="py-12 text-center text-xs text-gray-400">
                  <Loader2 className="mx-auto mb-1.5 h-4 w-4 animate-spin" />
                  Loading pricing rates…
                </div>
              ) : filteredPricingItems.length === 0 ? (
                <div className="rounded-md border border-gray-200 bg-gray-50/50 py-8 text-center text-xs text-gray-400">
                  No configured models match your search.
                </div>
              ) : (
                filteredPricingItems.map((item) => {
                  const key = `${item.provider}:${item.model}`
                  const isEditing = editingModelKey === key
                  const isSelfHosted = item.source === 'self_hosted'
                  const isCustom = item.source === 'custom' || item.isOverride
                  const isReverting = revertingKey === key

                  let sourceBadge = (
                    <span className="rounded-xs border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600">
                      Built-in
                    </span>
                  )
                  if (isSelfHosted) {
                    sourceBadge = (
                      <span className="rounded-xs border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                        Self-hosted · $0
                      </span>
                    )
                  } else if (isCustom) {
                    sourceBadge = (
                      <span className="rounded-xs border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">
                        Custom
                      </span>
                    )
                  }

                  return (
                    <div
                      key={key}
                      className="rounded-md border border-gray-200 bg-white p-3 shadow-2xs transition-colors hover:border-gray-300"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-mono text-xs font-semibold text-gray-900">
                            {item.model}
                          </div>
                          <div className="text-[11px] text-gray-400">
                            {item.provider}
                          </div>
                        </div>
                        <div>{sourceBadge}</div>
                      </div>

                      {/* Normal display */}
                      {!isEditing && (
                        <div className="mt-2.5 flex items-center justify-between text-xs">
                          <div className="text-gray-600">
                            {isSelfHosted ? (
                              <span className="font-medium text-emerald-600">$0 / 1k</span>
                            ) : (
                              <span>
                                ${item.inputPer1k.toFixed(4)} in · ${item.outputPer1k.toFixed(4)} out
                              </span>
                            )}
                          </div>
                          {!isSelfHosted && (
                            <button
                              type="button"
                              onClick={() => handleStartEdit(item)}
                              className="font-semibold text-[#3454cf] hover:underline"
                            >
                              {isCustom ? 'Edit' : 'Override'}
                            </button>
                          )}
                        </div>
                      )}

                      {/* Inline Edit Form */}
                      {isEditing && (
                        <div className="mt-3 border-t border-gray-100 pt-3">
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <label className="text-[10px] font-semibold text-gray-500 uppercase">
                                Input / 1k ($)
                              </label>
                              <input
                                type="number"
                                step="0.0001"
                                min="0"
                                value={editInputRate}
                                onChange={(e) => setEditInputRate(e.target.value)}
                                className="mt-1 w-full rounded-xs border border-gray-300 px-2 py-1 font-mono text-xs"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] font-semibold text-gray-500 uppercase">
                                Output / 1k ($)
                              </label>
                              <input
                                type="number"
                                step="0.0001"
                                min="0"
                                value={editOutputRate}
                                onChange={(e) => setEditOutputRate(e.target.value)}
                                className="mt-1 w-full rounded-xs border border-gray-300 px-2 py-1 font-mono text-xs"
                              />
                            </div>
                          </div>

                          <div className="mt-3 flex items-center justify-between">
                            <div>
                              {isCustom && onDeletePricing && (
                                <button
                                  type="button"
                                  onClick={() => void handleRevertPrice(item)}
                                  disabled={isReverting}
                                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-500 hover:text-gray-700 hover:underline"
                                >
                                  {isReverting ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : (
                                    <RotateCcw className="h-3 w-3" />
                                  )}
                                  Reset to built-in
                                </button>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={handleCancelEdit}
                                className="rounded-xs border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleSaveEdit(item)}
                                disabled={savingPricing}
                                className="inline-flex items-center gap-1 rounded-xs bg-[#3454cf] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#2a45b0]"
                              >
                                {savingPricing ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <Check className="h-3 w-3" />
                                )}
                                Save
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </div>

          {/* Pricing Drawer Footer */}
          <div className="border-t border-gray-100 pt-3 text-[11px] text-gray-400">
            Cost is stored per call, so price changes never rewrite history.
          </div>
        </div>
      )}
    </ResourceDrawer>
  )
}
